import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useClinicDashboard } from '../context/ClinicDashboardContext';
import ViewHeader from '../components/ViewHeader';
import KpiCard from '../components/KpiCard';
import CategoryBar from '../components/CategoryBar';
import DateFilter from '../components/DateFilter';
import EmptyState from '../components/EmptyState';
import { dashboardAPI } from '../../../../services/dashboardService';

const FinancialHealth = ({ onBack, currency = 'JOD' }) => {
    const { clinicId } = useParams();
    const { sharedMonthYear } = useClinicDashboard();
    const [data, setData] = useState(null);
    const [filters, setFilters] = useState({ year: sharedMonthYear.year });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchMetrics = async () => {
            try {
                setLoading(true);
                const response = await dashboardAPI.getFinancialHealthMetrics(clinicId, { year: filters.year });
                setData(response.data);
            } catch (err) {
                setError(err.message || 'Failed to load metrics');
            } finally {
                setLoading(false);
            }
        };

        if (clinicId) {
            fetchMetrics();
        }
    }, [clinicId, filters]);

    if (loading) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <ViewHeader title="Revenue and Financial Health Dashboard" subtitle="Loading..." onBack={onBack} />
                <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading financial data...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <ViewHeader title="Revenue and Financial Health Dashboard" subtitle="Error" onBack={onBack} />
                <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>{error}</div>
            </div>
        );
    }

    if (!data) return null;

    const { revenue_trends, revenue_by_category, revenue_overview, payment_collected } = data;

    const filterMonthlyChartData = (chartData, year) => {
        const now = new Date();
        if (year < now.getFullYear()) return chartData;
        // For current year, only show months up to current month
        return chartData.slice(0, now.getMonth() + 1);
    };

    const revenueData = filterMonthlyChartData(
        revenue_trends.map(t => ({ name: t.month, value: t.revenue })),
        filters.year
    );

    const categories = Object.keys(revenue_by_category).map(key => ({
        label: key,
        value: revenue_by_category[key].percentage
    }));

    const kpis = [
        { title: 'Total revenue', value: Number(revenue_overview.total_revenue.value).toLocaleString(undefined, { minimumFractionDigits: 3 }), unit: currency, change: `${Math.abs(revenue_overview.total_revenue.growth)}%`, changeType: revenue_overview.total_revenue.growth >= 0 ? 'up' : 'down' },
        { title: 'Revenue per visit', value: Number(revenue_overview.revenue_per_visit.value).toLocaleString(undefined, { minimumFractionDigits: 3 }), unit: currency, change: `${Math.abs(revenue_overview.revenue_per_visit.growth)}%`, changeType: revenue_overview.revenue_per_visit.growth >= 0 ? 'up' : 'down' },
        { title: 'Invoices issued', value: revenue_overview.invoices_issued.value.toLocaleString(), change: `${Math.abs(revenue_overview.invoices_issued.growth)}%`, changeType: revenue_overview.invoices_issued.growth >= 0 ? 'up' : 'down' },
    ];

    const hasRevenueData = revenueData.some(d => d.value > 0);
    const hasCategories = categories.some(c => c.value > 0);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <ViewHeader
                title="Revenue and Financial Health Dashboard"
                subtitle={`Year ${filters.year}`}
                onBack={onBack}
                filter={
                    <DateFilter
                        mode="year"
                        value={filters}
                        onChange={setFilters}
                    />
                }
            />

            {/* Top row: Revenue Trends | Revenue by Category */}
            <div className="clinic-dashboard-section-row">
                {/* Revenue Trends */}
                <div className="clinic-dashboard-section-card" style={{ animationDelay: '0ms' }}>
                    <div className="clinic-dashboard-section-title">Revenue trends</div>
                    {hasRevenueData ? (
                        <div className="clinic-dashboard-chart-wrap">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={revenueData} barSize={20} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                    <XAxis
                                        dataKey="name"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#9ca3af', fontSize: 12 }}
                                        dy={10}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#9ca3af', fontSize: 12 }}
                                    />
                                    <Tooltip
                                        cursor={{ fill: 'rgba(0,0,0,0.03)' }}
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                    />
                                    <Bar animationBegin={600} dataKey="value" fill="#f97316" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <EmptyState message="No revenue data for this year" />
                    )}
                </div>

                {/* Revenue by Category */}
                <div className="clinic-dashboard-section-card" style={{ animationDelay: '80ms' }}>
                    <div className="clinic-dashboard-section-title">Revenue by category</div>
                    {hasCategories ? (
                        <div className="clinic-dashboard-catbar-list">
                            {categories.map((cat, i) => <CategoryBar key={i} {...cat} />)}
                        </div>
                    ) : (
                        <EmptyState message="No category data for this year" />
                    )}
                </div>
            </div>

            {/* Bottom row: Revenue Overview | Payment Collected */}
            <div className="clinic-dashboard-section-row">
                {/* Revenue Overview */}
                <div className="clinic-dashboard-section-card" style={{ animationDelay: '160ms' }}>
                    <div className="clinic-dashboard-section-title">Revenue overview</div>
                    <div className="clinic-dashboard-kpi-row">
                        {kpis.map((kpi, i) => <KpiCard key={i} {...kpi} />)}
                    </div>
                </div>

                {/* Payment Collected */}
                <div className="clinic-dashboard-section-card" style={{ animationDelay: '240ms' }}>
                    <div className="clinic-dashboard-section-title">Payment collected</div>
                    <div className="clinic-dashboard-payment">
                        <div className="clinic-dashboard-payment-month">{payment_collected?.month}</div>
                        <div className="clinic-dashboard-payment-value">{Number(payment_collected?.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 3 })} {currency}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FinancialHealth;
